import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { toast } from "react-hot-toast";
import { getPlanDisplayName } from "../../services/razorpayService";

export const StoragePage = () => {
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    let mounted = true;
    const fetchStorage = async () => {
      try {
        setLoading(true);
        const token = Cookies.get("auth_token");
        if (!token) {
          toast.error("Session expired. Please log in again.");
          return (window.location.href = "/login");
        }

        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user_storage`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (json?.status === "1" && Array.isArray(json.data) && json.data.length > 0) {
          if (mounted) setPayload(json.data[0]);
        } else {
          toast.error(json?.message || "Failed to fetch storage");
        }
      } catch (err) {
        toast.error(err?.message || "Failed to fetch storage");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchStorage();
    return () => {
      mounted = false;
    };
  }, []);

  const formatBytes = (b) => {
    if (b == null) return "-";
    const gb = b / (1024 * 1024 * 1024);
    if (gb >= 1) return `${gb.toFixed(2)} GB`;
    const mb = b / (1024 * 1024);
    if (mb >= 1) return `${mb.toFixed(2)} MB`;
    const kb = b / 1024;
    return `${Math.round(kb)} KB`;
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="h-6 w-48 bg-gray-200 rounded animate-pulse mb-4" />
        <div className="space-y-2">
          <div className="h-24 bg-gray-100 rounded-lg animate-pulse" />
        </div>
      </div>
    );
  }

  const storage = payload?.storage || null;
  const percent = storage?.percent_used != null ? Math.min(1, Math.max(0, storage.percent_used)) : 0;
  const percentDisplay = Math.round(percent * 100);

  return (
    <div className="p-4 space-y-4">
      <h2 className="text-lg font-semibold">Storage</h2>

      {!payload ? (
        <div className="text-sm text-gray-600">No storage information available.</div>
      ) : (
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-2xl shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-sm text-gray-500">Plan</div>
                <div className="font-semibold">{getPlanDisplayName(payload.plan_type) || "-"}</div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">User ID</div>
                <div className="font-mono text-xs text-gray-700 truncate max-w-[200px]">{payload.user_id}</div>
              </div>
            </div>

            <div className="mt-2">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-gray-500">Usage</div>
                <div className="text-sm text-gray-600 font-medium">{percentDisplay}% used</div>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                <div
                  className="h-3 bg-[#6C45FF]"
                  style={{ width: `${percentDisplay}%` }}
                  aria-hidden
                />
              </div>

              <div className="flex items-center justify-between text-xs text-gray-500 mt-2">
                <div>Used: {formatBytes(storage?.used_bytes)}</div>
                <div>Limit: {storage?.limit_gb ? `${storage.limit_gb} GB` : formatBytes(storage?.limit_bytes)}</div>
              </div>
            </div>

            {payload.choose_plan_required ? (
              <div className="mt-4">
                <div className="text-sm text-red-600 font-medium mb-2">Choose a plan to continue</div>
                <button
                  type="button"
                  onClick={() => (window.location.href = "/payment")}
                  className="w-full bg-[#6C45FF] text-white font-semibold py-2 rounded-[10px]"
                >
                  Choose Plan
                </button>
              </div>
            ) : null}
          </div>

          <div className="text-sm text-gray-600">
            <div className="font-medium mb-1">Details</div>
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-white p-3 rounded-lg shadow-sm">
                <div className="text-xs text-gray-500">Limit (bytes)</div>
                <div className="font-semibold text-sm">{storage?.limit_bytes ?? "-"}</div>
              </div>
              <div className="bg-white p-3 rounded-lg shadow-sm">
                <div className="text-xs text-gray-500">Used (bytes)</div>
                <div className="font-semibold text-sm">{storage?.used_bytes ?? "-"}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoragePage;
